import { useEffect, useState } from 'react';

interface UseScrubberRevealAnimationOptions {
  /** Delay before the rail reveals. Defaults to 600ms (spec § 6.3). */
  delayMs?: number;
}

/** Orchestrates the scrubber's "no chrome until first beat" reveal.
 *
 *  Returns `revealed: false` on mount and flips to `true` after `delayMs`
 *  (default 600ms). Consumer applies the `vm-fade-up-blur` keyframe to
 *  the phase rail when revealed flips true.
 *
 *  `prefers-reduced-motion: reduce` short-circuits the timer and reveals
 *  immediately — no surprise late motion. */
export function useScrubberRevealAnimation(
  opts: UseScrubberRevealAnimationOptions = {},
): { revealed: boolean } {
  const { delayMs = 600 } = opts;
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setRevealed(true);
      return;
    }

    const t = setTimeout(() => setRevealed(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  return { revealed };
}
